// @flow
import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import moment from 'moment';
import { filter, sortBy, groupBy } from 'lodash';
import AppointmentCard from './AppointmentCard';
import AppointmentSkeleton from './AppointmentSkeleton';

type Props = {
  appointments: Object[],
  selectedDate: Object,
  clinic: Object,
  clinicDoctors: Object,
  filter: Object,
  loadingItems: Object,
  isFinatialEnabled: boolean,
  user: Object,
  isLoading: boolean,
  pending: boolean,
  failed: boolean,
  onShowAppointment: Function,
  onSelectDay: Function,
  _: Function,
};

const WeekView = ({
  appointments, selectedDate, clinic, clinicDoctors,
  filter: agendaFilter, loadingItems, isFinatialEnabled, user,
  isLoading, pending, failed, onShowAppointment, onSelectDay, _,
}: Props) => {
  const scrollRef = useRef(null);

  const weekDays = useMemo(() => {
    const startOfWeek = selectedDate.clone().startOf('isoWeek');
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.clone().add(i, 'days'));
    }
    return days;
  }, [selectedDate]);

  const weekAppointments = useMemo(() => {
    const weekStart = selectedDate.clone().startOf('isoWeek');
    const weekEnd = selectedDate.clone().endOf('isoWeek');
    return sortBy(
      filter(appointments, (apt) => {
        const aptDate = moment.utc(apt.start);
        return aptDate.isBetween(weekStart, weekEnd, 'day', '[]');
      }),
      (apt) => moment.utc(apt.start).valueOf(),
    );
  }, [appointments, selectedDate]);

  const groupedByDay = useMemo(
    () => groupBy(weekAppointments, (apt) => moment.utc(apt.start).format('YYYY-MM-DD')),
    [weekAppointments],
  );

  const todayStr = useMemo(() => moment().format('YYYY-MM-DD'), []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    // Once the week is on screen: scrolling over placeholders would scroll again when the cards land
    if (scrollRef.current && !isLoading && !pending) {
      const selectedDayStr = selectedDate.format('YYYY-MM-DD');
      const selectedSection = scrollRef.current.querySelector(`[data-day="${selectedDayStr}"]`);
      if (selectedSection) {
        selectedSection.scrollIntoView({ behavior: isFirstRender.current ? 'auto' : 'smooth', block: 'start' });
      } else {
        const todaySection = scrollRef.current.querySelector('.week-day-section.is-today');
        if (todaySection) {
          todaySection.scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      }
      isFirstRender.current = false;
    }
  }, [isLoading, pending, selectedDate]);

  const handleDayHeaderClick = useCallback((day) => {
    onSelectDay(day.format('YYYY-MM-DD'));
  }, [onSelectDay]);

  return (
    <div className="android-week-view" ref={scrollRef}>
      <div className="week-day-tabs">
        {weekDays.map((day) => {
          const dayStr = day.format('YYYY-MM-DD');
          const isToday = dayStr === todayStr;
          const isSelected = day.isSame(selectedDate, 'day');
          const hasAppts = (groupedByDay[dayStr] || []).length > 0;

          return (
            <div
              key={dayStr}
              className={
                'week-tab' +
                (isToday ? ' tab-today' : '') +
                (isSelected ? ' tab-selected' : '')
              }
              onClick={() => handleDayHeaderClick(day)}
            >
              <span className="tab-day-name">{day.format('ddd')}</span>
              <span className="tab-day-number">{day.format('DD')}</span>
              {hasAppts && <span className="tab-dot" />}
            </div>
          );
        })}
      </div>

      <div className="week-day-list" aria-busy={pending}>
        {weekDays.map((day) => {
          const dayStr = day.format('YYYY-MM-DD');
          const dayAppts = groupedByDay[dayStr] || [];
          const isToday = dayStr === todayStr;

          return (
            <div
              key={dayStr}
              data-day={dayStr}
              className={
                'week-day-section' +
                (isToday ? ' is-today' : '')
              }
            >
              <div
                className="week-day-header"
                onClick={() => handleDayHeaderClick(day)}
              >
                <span className="day-header-name">
                  {day.format('dddd')}
                </span>
                <span className="day-header-date">
                  {day.format('DD/MM')}
                </span>
                {isToday && <span className="day-header-today">{_('label/calendar_today')}</span>}
                <span className="day-header-count">
                  {dayAppts.length > 0 ? dayAppts.length : ''}
                </span>
              </div>
              {dayAppts.length > 0 ? (
                <div className="week-day-appointments">
                  {dayAppts.map((apt) => (
                    <AppointmentCard
                      key={apt.id}
                      appointment={apt}
                      clinicDoctors={clinicDoctors}
                      clinic={clinic}
                      filter={agendaFilter}
                      loadingItems={loadingItems}
                      isFinatialEnabled={isFinatialEnabled}
                      user={user}
                      onClick={onShowAppointment}
                      _={_}
                    />
                  ))}
                </div>
              ) : pending ? (
                <AppointmentSkeleton />
              ) : failed ? null : (
                <div className="week-day-empty">
                  <span>{_('label/no_appointments')}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// $FlowFixMe
export default React.memo(WeekView);
